import { useQuery } from "@tanstack/react-query";
import panelApi from "@/lib/panelApi";

export const useBaseComponents = () =>
  useQuery({ queryKey: ["panel-base-components"], queryFn: async () => (await panelApi.get("/panel/base-components")).data?.values || {} });

const pid = (base, etype, id) => `b:${base}:${etype}:${id}`;

// Componente da base → item no formato da config (kind/is_discount) + marcadores (inativo, quinzena, imposto, auto).
export const baseCompMaps = (baseComps = {}) => {
  const configById = {}, etypeOf = {}, disabled = {}, quinzena = {}, tax = {}, auto = {}, baseSalary = {};
  Object.entries(baseComps || {}).forEach(([b, byEt]) => Object.entries(byEt || {}).forEach(([et, comp]) => (comp?.items || []).forEach((c) => {
    const id = pid(b, et, c.id);
    configById[id] = { id, label: c.label, source: c.source, value: Number(c.value) || 0, kind: c.cls === "salary" ? "salary" : "benefit", is_discount: c.cls === "discount" || c.cls === "tax", active: c.enabled !== false };
    etypeOf[id] = et;
    if (c.enabled === false) disabled[id] = true;
    if (c.qz === "1" || c.qz === "2") quinzena[id] = Number(c.qz);
    if (c.cls === "tax") tax[id] = true;
    if (c.source === "bonus_auto") auto[id] = true;
    if (c.is_base_salary) baseSalary[id] = true;
  })));
  return { configById, etypeOf, disabled, quinzena, tax, auto, baseSalary };
};

// Itens da base para uma categoria; benefícios automáticos usam o valor calculado na linha (mesma origem).
export const baseItemsFor = (base, etype, comp, lineItems = []) => {
  const bySrc = {};
  lineItems.forEach((it) => { bySrc[it.source] = (bySrc[it.source] || 0) + (Number(it.value) || 0); });
  const used = new Set();
  return (comp?.items || []).map((c) => {
    const id = pid(base, etype, c.id);
    const auto = ["meal", "commute", "fuel"].includes(c.source);
    const value = auto ? (used.has(c.source) ? 0 : bySrc[c.source] || 0) : Number(c.value) || 0;
    if (auto) used.add(c.source);
    return { id, label: c.label, source: c.source, value, kind: c.cls === "salary" ? "salary" : "benefit", is_discount: c.cls === "discount" || c.cls === "tax", active: c.enabled !== false };
  });
};

// Troca os itens das linhas pela config própria da base do colaborador (quando existir para a categoria dele).
// PJ: o pagamento por entregas (pj_deliveries) é mantido, reescalado para o valor por entrega da base.
export const applyBaseComponents = (data, { baseComps, etypeOf, baseOf, pj = {} }) => {
  if (!data?.payroll?.lines || !baseComps || !Object.keys(baseComps).length) return data;
  let changed = false;
  const lines = data.payroll.lines.map((l) => {
    const et = (l.items || []).map((it) => etypeOf[it.id]).find(Boolean);
    const b = et ? baseOf(l.user_id) : null;
    const comp = b ? baseComps[b]?.[et] : null;
    if (!comp) return l;
    changed = true;
    const secRate = Number(pj.sectionRate?.[et]) || 0;
    const keep = (l.items || []).filter((it) => it.source === "pj_deliveries").map((it) => {
      if (pj.override?.[l.user_id] != null || comp.pj_rate == null || !secRate) return it;
      return { ...it, value: (Number(it.value) || 0) * (Number(comp.pj_rate) / secRate) };
    });
    return { ...l, items: [...keep, ...baseItemsFor(b, et, comp, l.items)], _cfgBase: b };
  });
  return changed ? { ...data, payroll: { ...data.payroll, lines } } : data;
};

export const pjRatesOf = (pjSettings) => {
  const sectionRate = {}, override = {};
  ["ciclista_pj", "motociclista_pj"].forEach((s) => {
    const sec = pjSettings?.sections?.[s];
    sectionRate[s] = sec?.valor_por_entrega;
    (sec?.drivers || []).forEach((d) => { if (d.pj_rate_override != null) override[d.id] = d.pj_rate_override; });
  });
  return { sectionRate, override };
};
